import { translations, TranslationDictionary } from './i18n/translations';
import { pricedTranslations, type SitePricing } from './pricing/apply-pricing';
import { bakedPricing } from './pricing/load-pricing';

export type LanguageCode = 'de' | 'en';

export class I18nManager {
  private currentLang: LanguageCode = 'de';
  /**
   * The pricing the copy's `{price:…}` placeholders are filled from (PRD-1553):
   * the version the build baked, until `setPricing` hands the live one over.
   */
  private pricing: SitePricing | null = typeof document === 'undefined' ? null : bakedPricing(document);
  private priced: { lang: LanguageCode; pricing: SitePricing | null; dict: TranslationDictionary } | null = null;

  constructor() {
    this.initLanguage();
  }

  private detectBrowserLanguage(): LanguageCode {
    // English mode is temporarily deactivated for go-live (German only, see issue #1174).
    return 'de';
  }

  private initLanguage(): void {
    // English mode is temporarily deactivated for go-live (German only, see issue #1174).
    this.currentLang = 'de';
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('crm-lang', 'de');
      } catch {}
    }
    this.applyLanguage('de');
  }

  public getLanguage(): LanguageCode {
    return this.currentLang;
  }

  public setLanguage(lang: LanguageCode): void {
    // English mode is temporarily deactivated for go-live (German only, see issue #1174).
    this.currentLang = 'de';
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('crm-lang', 'de');
      } catch {}
    }
    this.applyLanguage('de');
  }

  public getTranslations(): TranslationDictionary {
    return this.dictionaryFor(this.currentLang);
  }

  /** Re-render the copy with the live price book — read once on load. */
  public setPricing(pricing: SitePricing): void {
    this.pricing = this.pricing
      ? { version: pricing.version, lines: { ...this.pricing.lines, ...pricing.lines } }
      : pricing;
    this.applyLanguage(this.currentLang);
  }

  /** The dictionary as the page renders it: every amount filled from the pricing. */
  private dictionaryFor(lang: LanguageCode): TranslationDictionary {
    if (this.priced?.lang !== lang || this.priced.pricing !== this.pricing) {
      const dict = this.pricing ? pricedTranslations(translations[lang], this.pricing, lang) : translations[lang];
      this.priced = { lang, pricing: this.pricing, dict };
    }
    return this.priced.dict;
  }

  public applyLanguage(lang: LanguageCode): void {
    const dict = this.dictionaryFor(lang);
    document.documentElement.lang = lang;

    // Update Meta and SEO tags
    const pageTitleKey = document.documentElement.getAttribute('data-i18n-title');
    const pageDescKey =
      document.documentElement.getAttribute('data-i18n-desc') ||
      (pageTitleKey ? pageTitleKey.replace(/\.metaTitle$|\.title$/, '.metaDesc') : null);

    if (pageTitleKey) {
      const pageTitleVal = this.getNestedValue(dict, pageTitleKey);
      if (typeof pageTitleVal === 'string') {
        document.title = `${pageTitleVal} - ZETL CRM`;
        this.setMetaProperty('og:title', `${pageTitleVal} - ZETL CRM`);
      }
    } else {
      document.title = dict.meta.title;
      this.setMetaProperty('og:title', dict.meta.ogTitle);
    }

    let pageDescVal: unknown;
    if (pageDescKey) {
      pageDescVal = this.getNestedValue(dict, pageDescKey);
    }

    if (typeof pageDescVal === 'string') {
      this.setMetaContent('description', pageDescVal);
      this.setMetaProperty('og:description', pageDescVal);
    } else if (!pageTitleKey) {
      this.setMetaContent('description', dict.meta.description);
      this.setMetaProperty('og:description', dict.meta.ogDescription);
      this.setMetaContent('keywords', dict.meta.keywords);
    }

    this.setMetaProperty('og:locale', lang === 'de' ? 'de_DE' : 'en_US');

    // Update text content of elements with data-i18n attribute
    const elements = document.querySelectorAll<HTMLElement>('[data-i18n]');
    elements.forEach((el) => {
      const keyPath = el.getAttribute('data-i18n');
      if (!keyPath) return;

      const value = this.getNestedValue(dict, keyPath);
      if (typeof value === 'string') {
        el.textContent = value;
      }
    });

    // Update innerHTML of elements with data-i18n-html attribute
    const htmlElements = document.querySelectorAll<HTMLElement>('[data-i18n-html]');
    htmlElements.forEach((el) => {
      const keyPath = el.getAttribute('data-i18n-html');
      if (!keyPath) return;

      const value = this.getNestedValue(dict, keyPath);
      if (typeof value === 'string') {
        el.innerHTML = value;
      }
    });

    // Update input placeholders with data-i18n-placeholder
    const inputs = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-i18n-placeholder]');
    inputs.forEach((input) => {
      const keyPath = input.getAttribute('data-i18n-placeholder');
      if (!keyPath) return;

      const value = this.getNestedValue(dict, keyPath);
      if (typeof value === 'string') {
        input.placeholder = value;
      }
    });

    // Update language selector buttons state
    const langBtns = document.querySelectorAll<HTMLButtonElement>('.lang-select-btn');
    langBtns.forEach((btn) => {
      const btnLang = btn.getAttribute('data-lang');
      if (btnLang === lang) {
        btn.classList.add('bg-primary', 'text-white');
        btn.classList.remove('text-text-secondary', 'hover:bg-surface-3');
      } else {
        btn.classList.remove('bg-primary', 'text-white');
        btn.classList.add('text-text-secondary', 'hover:bg-surface-3');
      }
    });

    // Dispatch event so dynamic JS elements can re-render if needed
    window.dispatchEvent(new CustomEvent('languageChange', { detail: { lang, dict } }));
  }

  private setMetaContent(name: string, content: string): void {
    let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.name = name;
      document.head.appendChild(el);
    }
    el.content = content;
  }

  private setMetaProperty(property: string, content: string): void {
    let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('property', property);
      document.head.appendChild(el);
    }
    el.content = content;
  }

  private getNestedValue(obj: Record<string, any>, path: string): any {
    return path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
  }
}
